import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import * as yup from "yup";

import Button from "@components/buttons/Button";
import BaseErrors from "@components/fields/BaseErrors";
import RichTextarea from "@components/fields/RichTextarea";
import Form from "@components/forms/Form";
import BodyCopy from "@components/typography/BodyCopy";
import Heading2 from "@components/typography/Heading2";
import useBooleanToggle from "@hooks/useBooleanToggle";
import {
  GetClientWithAccountQuery,
  IssueReportCreateDocument,
  IssueReportCreateMutation,
  IssueReportCreateMutationVariables
} from "@typing/Generated";
import { UploadWithFile } from "@typing/Models";
import { handleMutationResponse } from "@utils/formUtils";

type Props = {
  client: GetClientWithAccountQuery["client"];
  journeyId: string;
};

type FormData = {
  directUploadBlobIds?: string[];
  message: string;
};

const AccountReportProblem = ({ journeyId }: Props) => {
  const intl = useIntl();
  const editorRefresher = useBooleanToggle();

  const defaultValues: FormData = {
    directUploadBlobIds: [],
    message: ""
  };

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      directUploadBlobIds: yup.array().defined().of(yup.string().defined()),
      message: yup
        .string()
        .required(intl.formatMessage({ id: "errors.message.body.blank" }))
        .test({
          exclusive: true,
          message: intl.formatMessage({ id: "errors.issueReport.message.blank" }),
          name: "not-empty-p-tags",
          test: value => value !== "<p></p>"
        })
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "all",
    resolver: yupResolver(schema)
  });

  const messageWatch = form.watch("message");
  const uploadsWatch = form.watch("directUploadBlobIds");

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [createIssueReport] = useMutation<IssueReportCreateMutation, IssueReportCreateMutationVariables>(
    IssueReportCreateDocument
  );

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    return createIssueReport({
      variables: {
        directUploadBlobIds: uploadsWatch,
        journeyId,
        message: messageWatch
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response?.data?.issueReportCreate,
        intl,
        onSuccess: async () => {
          form.reset();
          editorRefresher.toggle();
        },
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.message"
      });
    });
  }, [createIssueReport, editorRefresher, form, intl, journeyId, messageWatch, uploadsWatch]);

  const saveButtonDisabled = useMemo(
    () => !form?.formState.isValid || form?.formState.isSubmitting,
    [form?.formState.isSubmitting, form?.formState.isValid]
  );

  const handleUploadsChange = useCallback(
    (uploads: UploadWithFile[]) => {
      const blobIds = uploads.map(upload => upload.upload.signedBlobId);
      form.setValue("directUploadBlobIds", blobIds);
    },
    [form]
  );

  if (form.formState.isSubmitted) {
    return (
      <div className="ion-text-center ion-margin-top">
        <Heading2 bold color="gray">
          {intl.formatMessage({ id: "pages.client.account.reportProblem.title" })}
        </Heading2>
        <div className="ion-margin-bottom">
          <BodyCopy color="gray-500" size="small">
            {intl.formatMessage({ id: "pages.client.account.reportProblem.description" })}
          </BodyCopy>
        </div>
        <Button
          color="primary"
          onClick={() => {
            form.reset();
          }}
        >
          <FormattedMessage id="pages.client.account.reportProblem.another" />
        </Button>
      </div>
    );
  }

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />

      <RichTextarea<FormData>
        controls={["attachments", "submit"]}
        editorRefresher={editorRefresher}
        error={form.formState.errors.message}
        form={form}
        journeyId={journeyId}
        name="message"
        onUploadsChange={handleUploadsChange}
        placeholder={intl.formatMessage({ id: "pages.client.account.reportProblem.placeholder" })}
        size="xlarge"
        submitDisabled={saveButtonDisabled}
        uploadsWatch={uploadsWatch}
      />
    </Form>
  );
};

export default AccountReportProblem;
